var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import '../../../styles/widgets/clock/clockmanipulativepopup.scss';
import ButtonComponent from '../../../components/ButtonComponent';
import globalStore from '../../../store';
import Timer from './Timer';
import AnalogClock from './AnalogClock';
import CustomScrollbar from '../../../components/CustomScrollbar';
import { DataContext } from '../../../player/DataContext';
import { v4 as uuidv4 } from 'uuid';
import RadioButton from '../../../components/RadioButton';
import FooterActionsButtons from '../FooterActionsButtons';
import InputBox from '../../../components/InputBox';
import CheckBox from '../../../components/CheckBox';
import { saveState } from '../../../helper/stateManagerHelper';
import { getVisibleMidpoint } from '../../../helper/viewportHelper';
import { t } from 'i18next';
var clockStyleTooltipDirections = {
    noIndicator: 'topRight',
    arabic: 'top',
    number: 'top',
    numberWithMinutes: 'topLeft',
};
// -----------------------------------------------------------
// Custom Hook to handle click outside (Defined outside the component for clarity)
function useOutsideClick(ref, callback) {
    var memoizedCallback = useCallback(callback, [callback]);
    useEffect(function () {
        var handleClick = function (event) {
            if (ref.current && !ref.current.contains(event.target)) {
                memoizedCallback();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return function () {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [ref, memoizedCallback]);
}
var ClockManipulativePopup = function (props) {
    var _a;
    var deviceTime = useState(function () {
        var now = new Date();
        var hour12 = now.getHours() % 12 || 12;
        var minute = now.getMinutes();
        return {
            hour: hour12,
            minute: minute,
            period: (now.getHours() >= 12 ? 'PM' : 'AM'),
            hourAngle: (now.getHours() % 12) * 30 + minute * 0.5,
            minuteAngle: minute * 6,
        };
    })[0];
    var _b = props.data, data = _b === void 0 ? {
        id: 'timer_clock_' + uuidv4().replace(/-/g, ''),
        type: 'analog',
        movement: 'geared',
        hands: null, // need to be removed
        // handLabel: false,
        clockDesign: 'arabic',
        center: { x: 2500, y: 2500 },
        dimensions: { width: 360, height: 280 },
        scale: 1,
        timer_minutes: 10,
        timer_seconds: 0,
        analogType: 'arabic', // need to be removed
        hourhandVisible: true,
        minutehandVisible: true,
        handLabelVisible: false,
        timerSetterVisible: true,
        hourAngle: deviceTime.hourAngle,
        minuteAngle: deviceTime.minuteAngle,
        updateStyleOnwhiteboard: false,
        hour: deviceTime.hour,
        minute: deviceTime.minute,
        period: deviceTime.period,
        lock: { positioning: false, editing: false },
    } : _b;
    var _c = useContext(DataContext), setSelectedTool = _c.setSelectedTool, setSelectedManipulativeId = _c.setSelectedManipulativeId, setManiplulatorComponentVisible = _c.setManiplulatorComponentVisible, setActiveManipulator = _c.setActiveManipulator, selectedManipulativeId = _c.selectedManipulativeId, setManipulative = _c.setManipulative, manipulative = _c.manipulative, getPositionForNewItem = _c.getPositionForNewItem;
    var _d = useState(data), _clockData = _d[0], setClockData = _d[1];
    window['_clockData'] = _clockData; //--- IGNORE ---
    // State Variables for UI/Component Logic
    var _e = useState(_clockData.type), clockType = _e[0], setClockType = _e[1];
    var _f = useState(_clockData.movement), mode = _f[0], setMode = _f[1];
    var _g = useState(_clockData.hourhandVisible), hourhandV = _g[0], setHourhandVisible = _g[1];
    var _h = useState(_clockData.minutehandVisible), minutehandV = _h[0], setMinutehandVisible = _h[1];
    var _j = useState(_clockData.handLabelVisible), handLabelV = _j[0], sethandLabelVisible = _j[1];
    var _k = useState(true), showSetTime = _k[0], setShowSetTime = _k[1];
    var _l = useState(''), popupAnnounceText = _l[0], setPopupAnnounceText = _l[1];
    var _m = useState(String(_clockData.timer_minutes).padStart(2, '0')), inputMin = _m[0], setInputMin = _m[1];
    var _o = useState(String(_clockData.timer_seconds).padStart(2, '0')), inputSec = _o[0], setInputSec = _o[1];
    var childRef = useRef(null);
    var popupRef = useRef(null);
    var styleBtnRefs = useRef([]);
    // Initial structure for all clock options, currentValues updated in useEffect
    var _p = useState({
        clock: {
            title: t('text.clockPopupClock'),
            options: [t('text.clockPopupTimer'), t('text.clockPopupClock')],
            value: [
                { label: t('text.clockPopupTimer'), data: 'timer' },
                { label: t('text.clockPopupClock'), data: 'analog' },
            ],
            currentValue: _clockData.type,
        },
        movement: {
            title: t('text.clockPopupMovement'),
            options: [t('text.clockPopupGeared'), t('text.clockPopupFreeMoving')],
            value: [
                { label: t('text.clockPopupGeared'), data: 'geared' },
                { label: t('text.clockPopupFreeMoving'), data: 'notgeared' },
            ],
            currentValue: _clockData.movement,
        },
        hand: {
            title: t('text.clockPopupHands'),
            value: [
                { label: t('text.clockPopupHourHand'), data: 'hourH' },
                { label: t('text.clockPopupMinuteHand'), data: 'minuteH' },
                { label: t('text.clockPopupHandLabels'), data: 'handLabel' },
            ],
            currentValue: '', // Managed by individual visibility states
        },
        style: {
            title: t('text.clockPopupStyles'),
            value: [
                { label: t('text.clockStyleName_noIndicator'), data: 'noIndicator' },
                { label: t('text.clockStyleName_arabic'), data: 'arabic' },
                { label: t('text.clockStyleName_number'), data: 'number' },
                { label: t('text.clockStyleName_numberWithMinutes'), data: 'numberWithMinutes' },
            ],
            currentValue: _clockData.clockDesign,
        },
    }), clockObj = _p[0], setClockObj = _p[1];
    // Effect to synchronize component states with _clockData changes (e.g., from child or props)
    useEffect(function () {
        setClockType(_clockData.type);
        setMode(_clockData.movement);
        setHourhandVisible(_clockData.hourhandVisible);
        setMinutehandVisible(_clockData.minutehandVisible);
        sethandLabelVisible(_clockData.handLabelVisible);
        // Update clockObj current values based on props/initial data
        setClockObj(function (prev) { return (__assign(__assign({}, prev), { clock: __assign(__assign({}, prev.clock), { currentValue: _clockData.type }), movement: __assign(__assign({}, prev.movement), { currentValue: _clockData.movement }), style: __assign(__assign({}, prev.style), { currentValue: _clockData.clockDesign }) })); });
    }, [_clockData]); // Only re-run if _clockData changes
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var _q = globalStore.useStoreState(function (state) { return state.playerStore; }), zoomLevel = _q.zoomLevel, language = _q.language;
    // Re-translate the clockObj titles/options/labels when the language changes.
    // The state was initialized once with t() at mount, so without this the
    // popup keeps showing strings in the original language after Ctrl+Shift+L.
    // Only the visible-text fields are touched; currentValue and `data` keys
    // (which are language-agnostic enums) are preserved via prev.
    useEffect(function () {
        setClockObj(function (prev) { return ({
            clock: __assign(__assign({}, prev.clock), { title: t('text.clockPopupClock'), options: [t('text.clockPopupTimer'), t('text.clockPopupClock')], value: [
                    { label: t('text.clockPopupTimer'), data: 'timer' },
                    { label: t('text.clockPopupClock'), data: 'analog' },
                ] }),
            movement: __assign(__assign({}, prev.movement), { title: t('text.clockPopupMovement'), options: [t('text.clockPopupGeared'), t('text.clockPopupFreeMoving')], value: [
                    { label: t('text.clockPopupGeared'), data: 'geared' },
                    { label: t('text.clockPopupFreeMoving'), data: 'notgeared' },
                ] }),
            hand: __assign(__assign({}, prev.hand), { title: t('text.clockPopupHands'), value: [
                    { label: t('text.clockPopupHourHand'), data: 'hourH' },
                    { label: t('text.clockPopupMinuteHand'), data: 'minuteH' },
                    { label: t('text.clockPopupHandLabels'), data: 'handLabel' },
                ] }),
            style: __assign(__assign({}, prev.style), { title: t('text.clockPopupStyles'), value: [
                    { label: t('text.clockStyleName_noIndicator'), data: 'noIndicator' },
                    { label: t('text.clockStyleName_arabic'), data: 'arabic' },
                    { label: t('text.clockStyleName_number'), data: 'number' },
                    { label: t('text.clockStyleName_numberWithMinutes'), data: 'numberWithMinutes' },
                ] }),
        }); });
    }, [language]);
    var onBtnClick = function (type) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (type === 'done') {
            // 1. Get latest data from AnalogClock if it's the active view
            (_a = childRef.current) === null || _a === void 0 ? void 0 : _a.setDataToParent();
            var finalData = __assign(__assign({}, _clockData), { updateStyleOnwhiteboard: true, type: clockType, timer_is_running: false, timer_is_paused: false, timer_minutes: Number(inputMin), timer_seconds: Number(inputSec), timer_current_minutes: Number(inputMin), timer_current_seconds: Number(inputSec) });
            var _dimension = clockType === 'timer'
                ? { width: 291, height: 231 }
                : clockObj.style.currentValue === 'numberWithMinutes'
                    ? { width: 501.5, height: 501.5 }
                    : { width: 428, height: 428 };
            // Use getPositionForNewItem for new clocks, getVisibleMidpoint for editing
            var _j = selectedManipulativeId !== '' ? getVisibleMidpoint(zoomLevel) : getPositionForNewItem(), x = _j.x, y = _j.y;
            var newId = "".concat(clockType === 'analog' ? 'analog_clock_' : 'timer_clock_').concat(uuidv4().replace(/-/g, ''));
            if (selectedManipulativeId !== '') {
                // Update existing - find the old item first for undo/redo (outside setManipulative)
                var oldItem = manipulative.find(function (item) { return item.data.id === selectedManipulativeId; });
                var newItemType = "".concat(clockType === 'analog' ? 'analog' : 'clock');
                var updatedItem_1;
                if ((oldItem === null || oldItem === void 0 ? void 0 : oldItem.type) === newItemType) {
                    // Same type - update in place with dimensions
                    updatedItem_1 = __assign(__assign({}, oldItem), { data: __assign(__assign(__assign({}, oldItem.data), finalData), { dimensions: _dimension, lock: {
                                positioning: (_c = (_b = oldItem.data.lock) === null || _b === void 0 ? void 0 : _b.positioning) !== null && _c !== void 0 ? _c : false,
                                editing: (_e = (_d = oldItem.data.lock) === null || _d === void 0 ? void 0 : _d.editing) !== null && _e !== void 0 ? _e : false,
                            } }) });
                }
                else {
                    // Different type - create new with reset data
                    var _resetData = {
                        fractionOverlayParts: null,
                        fractionOverlayRotation: 0,
                        fractionOverlayColors: [],
                    };
                    updatedItem_1 = {
                        type: newItemType,
                        data: __assign(__assign(__assign({}, finalData), { id: newId, dimensions: _dimension, center: (_f = oldItem === null || oldItem === void 0 ? void 0 : oldItem.data.center) !== null && _f !== void 0 ? _f : { x: x, y: y }, lock: {
                                positioning: false,
                                editing: false,
                            } }), _resetData),
                    };
                }
                // Save state for undo/redo (outside setManipulative to avoid double calls)
                // Note: For manipulatives, stateManagerHelper spreads oldValue onto item.data,
                // so we need to save only the .data part, not the full item
                saveState({
                    type: selectedManipulativeId,
                    id: selectedManipulativeId.split('_').slice(1).join('_'),
                    propertyName: 'completeData',
                    old: JSON.stringify(oldItem === null || oldItem === void 0 ? void 0 : oldItem.data),
                    new: JSON.stringify(updatedItem_1 === null || updatedItem_1 === void 0 ? void 0 : updatedItem_1.data),
                });
                setManipulative(function (prev) {
                    return prev.map(function (item) { return (item.data.id === selectedManipulativeId ? updatedItem_1 : item); });
                });
                var focusId_1 = (_h = (_g = updatedItem_1 === null || updatedItem_1 === void 0 ? void 0 : updatedItem_1.data) === null || _g === void 0 ? void 0 : _g.id) !== null && _h !== void 0 ? _h : selectedManipulativeId;
                setSelectedTool([focusId_1]);
                // Focus the manipulative on the canvas after popup closes
                setTimeout(function () {
                    var _a;
                    var wrapper = document.querySelector("[data-manipulative-id=\"".concat(focusId_1, "\"]"));
                    var dummyBtn = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.dummy_btn');
                    (_a = (dummyBtn !== null && dummyBtn !== void 0 ? dummyBtn : wrapper)) === null || _a === void 0 ? void 0 : _a.focus();
                }, 400);
            }
            else {
                // Create new
                var _newItem_1 = {
                    type: "".concat(clockType === 'analog' ? 'analog' : 'clock'),
                    widgetId: "clock:".concat(uuidv4().replace(/-/g, '')),
                    data: __assign(__assign({}, finalData), { center: { x: x, y: y }, id: newId, dimensions: _dimension, lock: {
                            positioning: false,
                            editing: false,
                        } }),
                };
                // Save state for undo/redo
                saveState({
                    type: _newItem_1.data.id,
                    id: _newItem_1.data.id.split('_').slice(1).join('_'),
                    propertyName: 'create',
                    old: JSON.stringify(_newItem_1),
                });
                setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [_newItem_1], false); });
                // Auto-select the newly created clock
                setSelectedTool([_newItem_1.data.id]);
                // Focus the new manipulative on canvas after popup closes
                var newManipId_1 = _newItem_1.data.id;
                setTimeout(function () {
                    var _a;
                    var wrapper = document.querySelector("[data-manipulative-id=\"".concat(newManipId_1, "\"]"));
                    var dummyBtn = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.dummy_btn');
                    (_a = (dummyBtn !== null && dummyBtn !== void 0 ? dummyBtn : wrapper)) === null || _a === void 0 ? void 0 : _a.focus();
                }, 400);
            }
        }
        else {
            // Cancel: put back on whiteboard and restore button states
            if (selectedManipulativeId !== '' && data.type === 'timer' && clockType === 'timer') {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === selectedManipulativeId
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { updateStyleOnwhiteboard: true, timer_is_paused: false }) }) : item;
                    });
                });
            }
            else if (selectedManipulativeId !== '') {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === selectedManipulativeId
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { updateStyleOnwhiteboard: true }) }) : item;
                    });
                });
            }
        }
        setSelectedManipulativeId('');
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    var closePopup = useCallback(function () {
        // Only update the existing manipulative on close if one was selected
        if (selectedManipulativeId !== '') {
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === selectedManipulativeId
                        ? __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), _clockData), { updateStyleOnwhiteboard: true }) }) : item;
                });
            });
        }
    }, [selectedManipulativeId, _clockData, setManipulative]);
    // Invoke the custom hook for click away
    // useOutsideClick(popupRef, closePopup);
    // ------------------------------------------ Timer Functions ---------------------------------------------------------------------
    // ------------------------------------------ Rendering Helpers ---------------------------------------------------------------------
    /**
     * Renders a generic radio button group based on the clockObj key.
     * @param {keyof ClockObjMap} key - 'clock' or 'movement'
     */
    var renderRadioGroup = function (key) {
        var _a;
        var setting = clockObj[key];
        var isDisabled = clockType === 'timer' && key !== 'clock';
        var uniqueName = key + '_type'; // e.g., 'movement_type'
        return (_jsxs("fieldset", { className: "legend_wrapper", "aria-disabled": isDisabled || undefined, children: [_jsx("legend", { className: "".concat(isDisabled ? 'disableGrp' : ''), children: _jsxs("span", { className: "legendText", children: [" ", setting.title] }) }), _jsx(RadioButton, { id: uniqueName, options: (_a = setting.options) !== null && _a !== void 0 ? _a : [], selected: setting.currentValue === 'timer' || setting.currentValue === 'geared' ? 0 : 1, disabled: isDisabled, onChange: function (value) {
                        // const newType = diceTypeOptions[value] as typeof inputType;
                        // setInputType(newType);
                        // setDoneError(false);
                        // setCurrentFace(-1);
                        // setActiveMathInput(-1);
                        var _a, _b, _c, _d;
                        if (key === 'clock') {
                            var nextType_1 = (_b = (_a = setting.value) === null || _a === void 0 ? void 0 : _a[value]) === null || _b === void 0 ? void 0 : _b.data;
                            setClockType(nextType_1);
                            setClockObj(function (prev) { return (__assign(__assign({}, prev), { clock: __assign(__assign({}, prev.clock), { currentValue: nextType_1 }) })); });
                            setClockData(function (prev) { return (__assign(__assign({}, prev), { type: nextType_1 })); });
                            setPopupAnnounceText(t('text.clockPopupTypeChanged', {
                                type: nextType_1 === 'timer' ? t('text.clockPopupTimer') : t('text.clockPopupClock'),
                            }));
                            // Move focus to the preview area after type switch
                            // setTimeout(() => {
                            //   const preview = popupRef.current?.querySelector('.topColumnWrapper') as HTMLElement;
                            //   preview?.focus();
                            // }, 0);
                        }
                        else if (key === 'movement') {
                            var nextMode_1 = (_d = (_c = setting.value) === null || _c === void 0 ? void 0 : _c[value]) === null || _d === void 0 ? void 0 : _d.data;
                            setMode(nextMode_1);
                            setClockObj(function (prev) { return (__assign(__assign({}, prev), { movement: __assign(__assign({}, prev.movement), { currentValue: nextMode_1 }) })); });
                            setPopupAnnounceText(t('text.clockPopupMovementChanged', {
                                mode: nextMode_1 === 'geared'
                                    ? t('text.clockPopupGeared')
                                    : t('text.clockPopupFreeMoving'),
                            }));
                            setClockData(function (prev) {
                                return (__assign(__assign(__assign({}, prev), { movement: nextMode_1 }), (nextMode_1 === 'notgeared' && {
                                    digitalClockVisible: false,
                                    runJumpVisible: false,
                                    elapsedTimeDisplay: {
                                        shown: false,
                                        active: false,
                                        dragStarted: false,
                                        hoursCount: 0,
                                        referenceAngle: null,
                                        prevHourAngle: null,
                                        totalMinuteAngle: null,
                                        fraction: 0,
                                        startTime: { hourAngle: 0, minuteAngle: 0, secondAngle: 0 },
                                        endTime: { hourAngle: 0, minuteAngle: 0, secondAngle: 0 },
                                    },
                                })));
                            });
                        }
                    }, 
                    // => {
                    //   setInputType(diceTypeOptions[value] as typeof inputType);
                    // }}
                    className: "legendContent radioGroup" })] }));
    };
    /**
     * Renders the hand checkbox group specifically.
     */
    var renderHandCheckboxes = function () {
        var setting = clockObj.hand;
        var isDisabled = clockType === 'timer';
        var visibilityStates = {
            // Map data keys to their corresponding state variable
            hourH: hourhandV,
            minuteH: minutehandV,
            handLabel: handLabelV,
        };
        return (_jsxs("fieldset", { className: "legend_wrapper", "aria-disabled": isDisabled || undefined, children: [_jsx("legend", { className: "".concat(isDisabled ? 'disableGrp' : ''), children: _jsx("span", { className: "legendText", children: setting.title }) }), _jsx("div", { className: "legendContent", children: setting.value &&
                        setting.value.map(function (val) { return (_jsx(CheckBox, { id: "clock_".concat(val.data), checked: visibilityStates[val.data], label: val.label, onClick: function () {
                                // Checkboxes: Use local state to manage visibility
                                var newHourV = hourhandV, newMinuteV = minutehandV, newLabelV = handLabelV;
                                var selectedValue = val.data;
                                var handName = '';
                                var handVisible = false;
                                if (selectedValue === 'hourH') {
                                    newHourV = !hourhandV;
                                    setHourhandVisible(newHourV);
                                    handName = t('text.clockPopupHourHand');
                                    handVisible = newHourV;
                                }
                                else if (selectedValue === 'minuteH') {
                                    newMinuteV = !minutehandV;
                                    setMinutehandVisible(newMinuteV);
                                    handName = t('text.clockPopupMinuteHand');
                                    handVisible = newMinuteV;
                                }
                                else if (selectedValue === 'handLabel') {
                                    newLabelV = !handLabelV;
                                    sethandLabelVisible(newLabelV);
                                    handName = t('text.clockPopupHandLabels');
                                    handVisible = newLabelV;
                                }
                                if (handName) {
                                    setPopupAnnounceText(t('text.clockPopupHandToggled', {
                                        hand: handName,
                                        state: handVisible ? t('text.shown') : t('text.hidden'),
                                    }));
                                }
                                // Update the main data state once
                                setClockData(function (prev) {
                                    return (__assign(__assign({}, prev), { hourhandVisible: newHourV, minutehandVisible: newMinuteV, handLabelVisible: newLabelV }));
                                });
                            }, ariaLabel: val.label, className: "checkBoxGroup ".concat(isDisabled ? 'disableVerticalLine' : '', "\n                ").concat((clockType === 'timer'
                                ? true
                                : val.data === 'handLabel'
                                    ? !(_clockData.hourhandVisible || _clockData.minutehandVisible)
                                    : false)
                                ? 'disabledCheckBox'
                                : '', "\n                "), labelCSS: "checkBoxLabel", disabled: clockType === 'timer'
                                ? true
                                : val.data === 'handLabel'
                                    ? !(_clockData.hourhandVisible || _clockData.minutehandVisible)
                                    : false, tabIndex: clockType === 'timer'
                                ? -1
                                : val.data === 'handLabel' &&
                                    !(_clockData.hourhandVisible || _clockData.minutehandVisible)
                                    ? -1
                                    : undefined })); }) })] }));
    };
    useEffect(function () {
        if (clockType !== 'timer') {
            // setShowSetTime(false);
        }
    }, [clockType]);
    // ------------------------------------------ Main Render ---------------------------------------------------------------------
    return (_jsxs("div", { className: "clockWrapper", ref: popupRef, children: [_jsxs("div", { className: "scrollbarHolder", children: [_jsx(CustomScrollbar, { direction: "vertical", tabIndex: -1, children: _jsxs("div", { className: "scrollContentGroup", style: { display: 'flex', flexDirection: 'column' }, children: [_jsx("div", { className: "topColumnWrapper", tabIndex: -1, "aria-label": t('text.clockPopupPreview', {
                                        type: clockType === 'timer' ? t('text.clockPopupTimer') : t('text.clockPopupClock'),
                                    }), children: _jsx("div", { className: "clockType flexItem ".concat(_clockData.type), children: clockType === 'timer' ? (_jsxs(_Fragment, { children: [_jsx(Timer, { _clockData: __assign(__assign({}, _clockData), { timer_minutes: Number(inputMin), timer_seconds: Number(inputSec), timer_current_minutes: Number(inputMin), timer_current_seconds: Number(inputSec) }), onClockDataChange: setClockData, isOnWhiteBoard: false }), showSetTime && (_jsxs("div", { className: "setTimeRow flexItem", children: [_jsx("span", { className: "setTimeLabel", children: t('text.setTime') }), _jsxs("div", { className: "timeWrapper", children: [_jsx(InputBox, { defaultCSS: "timeInput", id: 'timeInputMinute', value: inputMin, min: 0, max: 60, type: 'text', isNumeric: true, maxLength: 2, inputControlledFromOutside: true, ignoreSpecialCharacter: true, ariaLabel: t('text.minutesInput'), onInput: function (minuteValId, minuteVal, event) {
                                                                        var numericValue = minuteVal.replace(/[^0-9]/g, '');
                                                                        var minVal = Math.max(0, Math.min(60, parseInt(numericValue) || 0));
                                                                        setInputMin(numericValue);
                                                                        if (minVal === 60) {
                                                                            setInputSec('00');
                                                                            setClockData(function (prev) {
                                                                                return (__assign(__assign({}, prev), { timer_minutes: 60, timer_seconds: 0 }));
                                                                            });
                                                                        }
                                                                        else {
                                                                            setClockData(function (prev) { return (__assign(__assign({}, prev), { timer_minutes: minVal })); });
                                                                        }
                                                                    }, onChange: function (minuteValId, event, minuteVal) {
                                                                        if (event === 'blur') {
                                                                            var minVal_1 = Math.max(0, Math.min(60, parseInt(minuteVal) || 0));
                                                                            var secVal = parseInt(inputSec) || 0;
                                                                            // Clamp to max 60:00
                                                                            if (minVal_1 >= 60) {
                                                                                minVal_1 = 60;
                                                                                setInputMin('60');
                                                                                setInputSec('00');
                                                                                setClockData(function (prev) {
                                                                                    return (__assign(__assign({}, prev), { timer_minutes: 60, timer_seconds: 0 }));
                                                                                });
                                                                                return;
                                                                            }
                                                                            var _minute = String(minVal_1).padStart(2, '0');
                                                                            setInputMin(_minute);
                                                                            // Clamp to min 00:01
                                                                            if (minVal_1 === 0 && secVal === 0) {
                                                                                setInputSec('01');
                                                                                setClockData(function (prev) {
                                                                                    return (__assign(__assign({}, prev), { timer_minutes: 0, timer_seconds: 1 }));
                                                                                });
                                                                            }
                                                                            else {
                                                                                setClockData(function (prev) { return (__assign(__assign({}, prev), { timer_minutes: minVal_1 })); });
                                                                            }
                                                                        }
                                                                    } }), _jsx("span", { children: ":" }), _jsx(InputBox, { defaultCSS: "timeInput", id: 'timeInputSecond', value: inputSec, min: 0, max: 59, type: 'text', isNumeric: true, maxLength: 2, ignoreSpecialCharacter: true, inputControlledFromOutside: true, ariaLabel: t('text.secondsInput'), onInput: function (secondValId, secondVal, event) {
                                                                        var numericValue = secondVal.replace(/[^0-9]/g, '');
                                                                        var minVal = parseInt(inputMin) || 0;
                                                                        var secVal = Math.max(0, Math.min(59, parseInt(numericValue) || 0));
                                                                        // If minutes is 60, seconds must be 00
                                                                        if (minVal >= 60) {
                                                                            setInputSec('00');
                                                                            return;
                                                                        }
                                                                        setInputSec(numericValue);
                                                                        setClockData(function (prev) { return (__assign(__assign({}, prev), { timer_seconds: secVal })); });
                                                                    }, onChange: function (secondValId, event, secondVal) {
                                                                        if (event === 'blur') {
                                                                            var minVal = parseInt(inputMin) || 0;
                                                                            var secVal_1 = Math.max(0, Math.min(59, parseInt(secondVal) || 0));
                                                                            // If minutes is 60, force seconds to 00
                                                                            if (minVal >= 60) {
                                                                                setInputSec('00');
                                                                                setClockData(function (prev) { return (__assign(__assign({}, prev), { timer_seconds: 0 })); });
                                                                                return;
                                                                            }
                                                                            // Clamp to min 00:01
                                                                            if (minVal === 0 && secVal_1 === 0) {
                                                                                secVal_1 = 1;
                                                                            }
                                                                            var _second = String(secVal_1).padStart(2, '0');
                                                                            setInputSec(_second);
                                                                            setClockData(function (prev) { return (__assign(__assign({}, prev), { timer_seconds: secVal_1 })); });
                                                                        }
                                                                    } })] })] }))] })) : (_jsx("div", { className: "analog_clockHolderDiv", children: _jsx(AnalogClock, { _clockData: __assign(__assign({}, _clockData), { scale: 1, dimensions: clockObj.style.currentValue === 'numberWithMinutes'
                                                        ? { width: 377.5, height: 377.5 }
                                                        : { width: 322, height: 322 }, 
                                                    // clockObj.style.currentValue === 'numberWithMinutes'
                                                    //   ? { width: 330, height: 330 }
                                                    //   : { width: 283, height: 283 },
                                                    hour: deviceTime.hour, minute: deviceTime.minute, hourAngle: deviceTime.hourAngle, minuteAngle: deviceTime.minuteAngle }), isOnWhiteBoard: false }) })) }) }), _jsxs("div", { className: "clockDataHolder", children: [_jsx("hr", { className: "customHr", "aria-hidden": "true" }), renderRadioGroup('clock'), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), renderRadioGroup('movement'), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), renderHandCheckboxes(), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), _jsxs("fieldset", { className: "legend_wrapper", "aria-disabled": clockType === 'timer' || undefined, children: [_jsx("legend", { className: "legendText iconsLegendText ".concat(clockType === 'timer' ? 'disableGrp' : ''), children: _jsx("span", { children: clockObj.style.title }) }), _jsx("div", { className: "legendContent", children: clockObj.style.value &&
                                                        ((_a = clockObj.style.value) === null || _a === void 0 ? void 0 : _a.map(function (val, idx) {
                                                            var _a, _b, _c;
                                                            var isSelected = clockObj.style.currentValue === val.data && clockType === 'analog';
                                                            var selectedIdx = (_b = (_a = clockObj.style.value) === null || _a === void 0 ? void 0 : _a.findIndex(function (v) { return v.data === clockObj.style.currentValue; })) !== null && _b !== void 0 ? _b : -1;
                                                            var rovingTabIndex = clockType === 'timer'
                                                                ? -1
                                                                : selectedIdx >= 0
                                                                    ? idx === selectedIdx
                                                                        ? 0
                                                                        : -1
                                                                    : idx === 0
                                                                        ? 0
                                                                        : -1;
                                                            return (_jsx("div", { className: "iconStyleGroup ".concat(clockType === 'timer' ? 'disableVerticalLine' : ''), children: _jsx(ButtonComponent, { defaultCSS: "clockIcons ".concat(isSelected ? 'active' : ''), normalCSS: "clockIcons", type: "imageType1", imgSrc: ((_c = btnConfig["".concat(val.data, "clockIcon")]) === null || _c === void 0 ? void 0 : _c.imgSrc) || '', title: "text.clockStyleName_".concat(val.data), showToolTip: true, direction: clockStyleTooltipDirections[val.data], ariaLabel: "text.clockStyleName_".concat(val.data), role: "radio", ariaChecked: isSelected, tabIndex: rovingTabIndex, reference: function (el) {
                                                                        styleBtnRefs.current[idx] = el;
                                                                    }, isParentRelative: true, onKeyDown: function (e) {
                                                                        var _a, _b, _c, _d;
                                                                        if (clockType === 'timer')
                                                                            return;
                                                                        var len = (_b = (_a = clockObj.style.value) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
                                                                        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                                                                            e.preventDefault();
                                                                            var next = (idx + 1) % len;
                                                                            (_c = styleBtnRefs.current[next]) === null || _c === void 0 ? void 0 : _c.focus();
                                                                        }
                                                                        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                                                                            e.preventDefault();
                                                                            var prev = (idx - 1 + len) % len;
                                                                            (_d = styleBtnRefs.current[prev]) === null || _d === void 0 ? void 0 : _d.focus();
                                                                        }
                                                                    }, clickHandler: function () {
                                                                        setClockData(function (prev) {
                                                                            return (__assign(__assign({}, prev), { clockDesign: val.data, analogType: val.data }));
                                                                        });
                                                                        setPopupAnnounceText(t('text.clockPopupStyleChanged', {
                                                                            style: t("text.clockStyleName_".concat(val.data)),
                                                                        }));
                                                                    }, disabled: clockType === 'timer' }) }, val.data));
                                                        })) })] }), _jsx("hr", { className: "customHr", "aria-hidden": "true" })] })] }) }), _jsx(FooterActionsButtons, { disableDoneBtn: false, onActionBtnClick: function (type) {
                            if (type === 'done') {
                                onBtnClick('done');
                            }
                            else {
                                onBtnClick('cancel');
                            }
                        } })] }), _jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '0.0625rem',
                    height: '0.0625rem',
                    margin: '-0.0625rem',
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: popupAnnounceText })] }));
};
export default ClockManipulativePopup;
